import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPricingPlan } from "../../utils/wellspoken.api";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Keyboard, Autoplay } from "swiper/modules";
import "swiper/css";

const PricingSection = () => {
  const navigate = useNavigate();
  const [pricingPlans, setPricingPlans] = useState([]);

  useEffect(() => {
    const loadPricing = async () => {
      const data = await fetchPricingPlan();

      const normalized = data
        .filter((plan) => plan.planStatus === "Active")
        .map((plan, index) => {
          let features = [];
          try {
            if (plan.planFeature?.trim().startsWith("[")) {
              features = JSON.parse(plan.planFeature);
            } else {
              features = plan.planFeature
                .split(/,|\n/)
                .map((f) => f.trim())
                .filter(Boolean);
            }
          } catch {
            features = [];
          }

          return {
            id: plan.planId,
            name: plan.planName,
            description: plan.pricetype,
            price: `₹${plan.price}`,
            duration: plan.pricetype,
            features,
            highlighted: index === 0,
          };
        });

      setPricingPlans(normalized);
    };

    loadPricing();
  }, []);

  const handleEnroll = (plan) => {
    navigate("/contact", {
      state: {
        type: plan.name,
      },
    });
  };

  return (
    <section
      className="relative py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-hidden"
      aria-labelledby="pricing-heading"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 left-1/4 w-72 h-72 sm:w-96 sm:h-96 bg-blue-300 rounded-full blur-3xl opacity-30" />
        <div className="absolute -bottom-24 right-1/4 w-80 h-80 sm:w-[500px] sm:h-[500px] bg-purple-300 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-14 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 bg-white rounded-full shadow border mb-5">
            <span aria-hidden>💎</span>
            <span className="text-xs sm:text-sm font-bold text-purple-600 uppercase tracking-wider">
              Pricing Plans
            </span>
          </div>

          <h2
            id="pricing-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 sm:mb-6"
          >
            Simple, Transparent{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Pricing
            </span>
          </h2>

          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
            Choose a plan that fits your learning goals and schedule.
          </p>
        </header>

        {/* Pricing Cards Grid */}
        <div className="grid gap-8 sm:gap-10 md:grid-cols-2 lg:grid-cols-3">
          {pricingPlans.map((plan) => (
            <article
              key={plan.id}
              className="
            bg-white
            rounded-[2rem]
            px-6 py-8 sm:p-8 lg:p-10
            shadow-lg
            transition-all duration-300
            lg:hover:-translate-y-2
            lg:hover:shadow-2xl
          "
            >
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl flex items-center justify-center text-3xl sm:text-4xl bg-gradient-to-br from-blue-100 to-indigo-100 shadow">
                  📦
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 text-center mb-2">
                {plan.name}
              </h3>

              {/* Price */}
              <div className="text-center mb-8 sm:mb-10">
                <div className="flex justify-center items-end gap-1 sm:gap-2">
                  <span className="text-4xl sm:text-5xl font-black text-gray-900">
                    {plan.price}
                  </span>
                  <span className="text-sm sm:text-lg text-gray-500">
                    /{plan.duration}
                  </span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex gap-3">
                    <span
                      className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-md bg-blue-100 text-blue-600 font-bold text-sm"
                      aria-hidden
                    >
                      ✓
                    </span>
                    <span className="text-sm sm:text-base text-gray-700">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleEnroll(plan)}
                className="
              w-full
              px-8 py-3
              text-sm sm:text-base font-semibold text-white
              rounded-full
              bg-[linear-gradient(to_bottom,#b30000_0%,#ff3b3b_45%,#d10000_55%,#7a0000_100%)]
              shadow-[0_8px_18px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.35)]
              transition-all duration-300 ease-out
              hover:brightness-110
              hover:shadow-[0_12px_28px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.45)]
              lg:hover:-translate-y-[2px]
              active:translate-y-[1px]
            "
                aria-label={`Enroll in ${plan.name}`}
              >
                Enroll
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>


  );
};

export default PricingSection;
